/**
 * Modular Reservations - Main orchestrator component
 * Manages tab routing and coordinates sub-modules
 */

import React, { useState, useEffect, useMemo } from 'react';
import { TruckIcon, CalendarDaysIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import useUserPreferences from '../../hooks/useUserPreferences';
import { ReservationsProvider } from './reservations.shared/context/ReservationsContext';
import ReservationFiltersHeader from './reservations.shared/components/ReservationFiltersHeader';
import ReservationsItemsTab from './reservations.items/components/ReservationsItemsTab';
import ReservationsServicesTab from './reservations.services/components/ReservationsServicesTab';
import ReservationsLogTab from './reservations.log/components/ReservationsLogTab';
import LoadingSpinner from '../common/LoadingSpinner';
import axios from 'axios';

const ModularReservations = () => {
  const { isAuthenticated, token } = useAuth();
  const { preferences, updateReservationsTabOrder, loading: preferencesLoading } = useUserPreferences();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Module visibility settings
  const [moduleSettings, setModuleSettings] = useState({
    module_reservations_items_enabled: true,
    module_reservations_services_enabled: true,
  });
  const [moduleSettingsLoading, setModuleSettingsLoading] = useState(true);

  // Default tab order - memoized to prevent useEffect dependency changes
  const defaultTabOrder = useMemo(() => ['reservations', 'services', 'log'], []);

  // Drag and drop state - now using server-side preferences
  const [tabOrder, setTabOrder] = useState(defaultTabOrder);
  const [activeTab, setActiveTab] = useState('reservations'); // Set to reservations tab by default
  const [draggedTab, setDraggedTab] = useState(null);
  const [dragOverTab, setDragOverTab] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInitializedActiveTab, setHasInitializedActiveTab] = useState(false);
  
  // Shared filter state for all tabs
  const [globalFilters, setGlobalFilters] = useState({
    status: 'all',
    searchTerm: '',
    serviceFilter: '',
    customerFilter: ''
  });
  
  // Services and customers for filters
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Fetch module visibility settings
  useEffect(() => {
    const fetchModuleSettings = async () => {
      try {
        const response = await axios.get('/api/settings/module-visibility', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setModuleSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching module visibility settings:', error);
        // Keep defaults on error
      } finally {
        setModuleSettingsLoading(false);
      }
    };

    if (token) {
      fetchModuleSettings();
    }
  }, [token]);

  // Filter tab order based on module visibility settings
  const visibleTabs = useMemo(() => {
    let filtered = [...defaultTabOrder];

    // Remove reservations tab if Items module is disabled
    if (!moduleSettings.module_reservations_items_enabled) {
      filtered = filtered.filter(tab => tab !== 'reservations');
    }

    // Remove services tab if Services module is disabled
    if (!moduleSettings.module_reservations_services_enabled) {
      filtered = filtered.filter(tab => tab !== 'services');
    }

    return filtered;
  }, [defaultTabOrder, moduleSettings]);

  // Reservation counts from API (no hardcoded mock data)
  const [reservationCounts, setReservationCounts] = useState({
    all: 0,
    active: 0,
    completed: 0,
    cancelled: 0,
    confirmed: 0,
    pendingPayment: 0,
    itemsAll: 0,
    itemsActive: 0,
    itemsCompleted: 0,
    itemsCancelled: 0,
    servicesAll: 0,
    servicesConfirmed: 0,
    servicesPendingPayment: 0,
    servicesCompleted: 0,
    servicesCancelled: 0
  });

  // Fetch services and customers for filter dropdowns
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [servicesResponse, customersResponse] = await Promise.all([
          axios.get('/api/inventory/services'),
          axios.get('/api/customers')
        ]);

        console.log('🔍 Services API Response:', servicesResponse.data);
        console.log('🔍 Customers API Response:', customersResponse.data);

        setServices(Array.isArray(servicesResponse.data) ? servicesResponse.data : []);
        setCustomers(Array.isArray(customersResponse.data?.customers) ? customersResponse.data.customers : []);
      } catch (error) {
        console.error('Failed to fetch filter data:', error);
        // Ensure arrays remain as arrays even on error
        setServices([]);
        setCustomers([]);
      }
    };

    if (isAuthenticated) {
      fetchFilterData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchReservationCounts = async () => {
      try {
        const response = await axios.get('/api/reservations/integration/stats/overview');
        const stats = response.data;
        
        // Set global counts from API response
        const allCounts = {
          // Overall counts
          all: stats.total || 0,
          active: stats.active || 0,
          completed: stats.completed || 0,
          cancelled: stats.cancelled || 0,
          confirmed: stats.confirmed || 0,
          pendingPayment: stats.pendingPayment || 0,
          
          // Items-specific counts
          itemsAll: stats.breakdown?.items?.total || 0,
          itemsActive: stats.breakdown?.items?.active || 0,
          itemsCompleted: stats.breakdown?.items?.completed || 0,
          itemsCancelled: stats.breakdown?.items?.cancelled || 0,
          
          // Services-specific counts  
          servicesAll: stats.breakdown?.services?.total || 0,
          servicesConfirmed: stats.breakdown?.services?.confirmed || 0,
          servicesPendingPayment: stats.breakdown?.services?.pendingPayment || 0,
          servicesCompleted: stats.breakdown?.services?.completed || 0,
          servicesCancelled: stats.breakdown?.services?.cancelled || 0
        };
        
        setReservationCounts(allCounts);
      } catch (error) {
        console.error('Failed to fetch reservation counts:', error);
        // Keep at 0 if API fails
        setReservationCounts({
          all: 0,
          active: 0,
          completed: 0,
          cancelled: 0,
          confirmed: 0,
          pendingPayment: 0,
          itemsAll: 0,
          itemsActive: 0,
          itemsCompleted: 0,
          itemsCancelled: 0,
          servicesAll: 0,
          servicesConfirmed: 0,
          servicesPendingPayment: 0,
          servicesCompleted: 0,
          servicesCancelled: 0
        });
      }
    };

    if (isAuthenticated) {
      setLoading(false);
      fetchReservationCounts();
    } else {
      // Clear counts when not authenticated
      setReservationCounts({
        all: 0,
        active: 0,
        completed: 0,
        cancelled: 0,
        confirmed: 0,
        pendingPayment: 0,
        itemsAll: 0,
        itemsActive: 0,
        itemsCompleted: 0,
        itemsCancelled: 0,
        servicesAll: 0,
        servicesConfirmed: 0,
        servicesPendingPayment: 0,
        servicesCompleted: 0,
        servicesCancelled: 0
      });
    }
  }, [isAuthenticated]);

  // Sync tab order with user preferences and set initial active tab
  useEffect(() => {
    if (!preferencesLoading && !moduleSettingsLoading) {
      const finalTabOrder = preferences.reservationsTabOrder
        ? preferences.reservationsTabOrder.filter(tab => visibleTabs.includes(tab))
        : visibleTabs;

      setTabOrder(finalTabOrder.length > 0 ? finalTabOrder : visibleTabs);

      // Only set active tab to leftmost on first load, not on every preference change
      // This prevents overriding manual tab clicks
      if (!hasInitializedActiveTab) {
        const leftmostTab = finalTabOrder[0] || visibleTabs[0];
        setActiveTab(leftmostTab);
        setHasInitializedActiveTab(true);

        console.log('🔄 Reservations tab order loaded:', finalTabOrder);
        console.log('🎯 Reservations active tab set to leftmost:', leftmostTab);
      } else {
        console.log('🔄 Reservations tab order updated without changing active tab:', finalTabOrder);
      }
    }
  }, [preferences.reservationsTabOrder, preferencesLoading, moduleSettingsLoading, visibleTabs, hasInitializedActiveTab]);

  // Cleanup effect for touch timers
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  // Save tab order to server and update local state
  const saveTabOrder = (newOrder) => {
    setTabOrder(newOrder);
    updateReservationsTabOrder(newOrder);

    // If the currently active tab is still in the new order, keep it active
    // Otherwise, switch to the leftmost tab
    if (!newOrder.includes(activeTab)) {
      setActiveTab(newOrder[0]);
      console.log('🎯 Active tab switched to leftmost after reorder:', newOrder[0]);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, tabId) => {
    setDraggedTab(tabId);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tabId);
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggedTab(null);
    setDragOverTab(null);
    setIsDragging(false);
    // Reset visual feedback
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, tabId) => {
    e.preventDefault();
    if (draggedTab !== tabId) {
      setDragOverTab(tabId);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear drag over if we're actually leaving the tab area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTab(null);
    }
  };

  const handleDrop = (e, targetTabId) => {
    e.preventDefault();

    if (!draggedTab || draggedTab === targetTabId) {
      return;
    }

    const currentOrder = [...tabOrder];
    const draggedIndex = currentOrder.indexOf(draggedTab);
    const targetIndex = currentOrder.indexOf(targetTabId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged tab from current position
      currentOrder.splice(draggedIndex, 1);
      // Insert dragged tab at target position
      currentOrder.splice(targetIndex, 0, draggedTab);

      saveTabOrder(currentOrder);
    }

    setDragOverTab(null);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e, tabId) => {
    const timer = setTimeout(() => {
      setDraggedTab(tabId);
      setIsDragging(true);
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Add visual feedback
      e.target.style.transform = 'scale(1.05)';
      e.target.style.zIndex = '1000';
    }, 500); // 500ms long press

    setLongPressTimer(timer);
  };

  const handleTouchEnd = (e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    if (isDragging) {
      // Reset visual feedback
      e.target.style.transform = '';
      e.target.style.zIndex = '';
      setDraggedTab(null);
      setIsDragging(false);
    }
  };

  const handleTouchMove = (e, targetTabId) => {
    if (!isDragging || !draggedTab) return;

    e.preventDefault();

    // Get touch position
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    // Find the tab element
    const tabElement = element?.closest('[data-tab-id]');
    if (tabElement) {
      const newTargetTabId = tabElement.getAttribute('data-tab-id');
      if (newTargetTabId && newTargetTabId !== draggedTab) {
        setDragOverTab(newTargetTabId);

        // Perform the reorder
        const currentOrder = [...tabOrder];
        const draggedIndex = currentOrder.indexOf(draggedTab);
        const targetIndex = currentOrder.indexOf(newTargetTabId);

        if (draggedIndex !== -1 && targetIndex !== -1) {
          currentOrder.splice(draggedIndex, 1);
          currentOrder.splice(targetIndex, 0, draggedTab);
          saveTabOrder(currentOrder);
        }
      }
    }
  };

  // Tab configuration
  const tabConfigs = {
    reservations: {
      id: 'reservations',
      label: 'Reservations',
      component: ReservationsItemsTab,
      icon: TruckIcon
    },
    services: {
      id: 'services',
      label: 'Services',
      component: ReservationsServicesTab,
      icon: CalendarDaysIcon
    },
    log: {
      id: 'log',
      label: 'Activity Log',
      component: ReservationsLogTab,
      icon: ClipboardDocumentListIcon
    }
  };

  // Create ordered tabs array based on tabOrder state
  const tabs = tabOrder.map(tabId => tabConfigs[tabId]).filter(Boolean);

  if (loading || preferencesLoading || moduleSettingsLoading || !activeTab) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Reservations</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ReservationsProvider>
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-3 sm:py-4">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reservations Management</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Manage item rentals, service bookings, and activity logs</p>
            </div>
            
            {/* Tab Navigation - Mobile Optimized with Horizontal Scroll */}
            <div className="border-b border-gray-200">
              <div className="overflow-x-auto overflow-y-hidden -mx-4 sm:mx-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                <nav className="-mb-px flex space-x-6 min-w-max px-4 sm:px-0" style={{ width: 'max-content', minWidth: '100%' }}>
              {tabs.map((tab) => {
                const isBeingDragged = draggedTab === tab.id;
                const isDragTarget = dragOverTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    data-tab-id={tab.id}
                    draggable={true}
                    onClick={() => !isDragging && setActiveTab(tab.id)}
                    onDragStart={(e) => handleDragStart(e, tab.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, tab.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, tab.id)}
                    className={`
                      flex items-center space-x-2 py-3 sm:py-4 px-3 sm:px-1 border-b-2 font-medium text-sm
                      cursor-pointer whitespace-nowrap flex-shrink-0
                      ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                      ${isBeingDragged
                        ? 'opacity-50 transform scale-95'
                        : ''
                      }
                      ${isDragTarget
                        ? 'bg-blue-50 border-blue-300 scale-105'
                        : ''
                      }
                      ${isDragging && !isBeingDragged
                        ? 'opacity-75'
                        : ''
                      }
                    `}
                  >
                    <tab.icon className="h-5 w-5 sm:h-5 sm:w-5" />
                    <span>{tab.label}</span>
                    {/* Drag indicator */}
                    <div className="opacity-0 hover:opacity-50 ml-1">
                      <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 15h18v-2H3v2zm0 4h18v-2H3v2zm0-8h18V9H3v2zm0-6v2h18V5H3z"/>
                      </svg>
                    </div>
                  </button>
                );
              })}
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Global Filters - Show for reservations and services tabs */}
        {(activeTab === 'reservations' || activeTab === 'services') && (
          <ReservationFiltersHeader
            filters={globalFilters}
            onFiltersChange={setGlobalFilters}
            showSearchReservations={true}
            showServiceFilter={activeTab === 'services'}
            showCustomerFilter={true}
            reservationCounts={activeTab === 'services' ? {
              all: reservationCounts.servicesAll || 0,
              confirmed: reservationCounts.servicesConfirmed || 0,
              pendingPayment: reservationCounts.servicesPendingPayment || 0,
              completed: reservationCounts.servicesCompleted || 0,
              cancelled: reservationCounts.servicesCancelled || 0
            } : {
              all: reservationCounts.itemsAll || 0,
              active: reservationCounts.itemsActive || 0,
              completed: reservationCounts.itemsCompleted || 0,
              cancelled: reservationCounts.itemsCancelled || 0
            }}
            services={services}
            customers={customers}
            reservationType={activeTab === 'services' ? 'services' : 'items'}
          />
        )}

        {/* Tab Content - Mobile Optimized */}
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-96">
            {(() => {
              const activeTabConfig = tabs.find(tab => tab.id === activeTab);
              if (!activeTabConfig) return null;

              const TabComponent = activeTabConfig.component;
              return (
                <div className="p-4 sm:p-6">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <TabComponent globalFilters={globalFilters} services={services} customers={customers} />
                  </React.Suspense>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </ReservationsProvider>
  );
};

export default ModularReservations;